/**
 * AdminCommandPalette — Cmd+K / Ctrl+K quick palette for admin.
 *
 * Tres grupos:
 *   1. Páginas del admin — todas las rutas visibles para el rol actual,
 *      con el nombre del grupo del sidebar como hint (Conversión, Sitio…).
 *   2. Acciones rápidas — "Ver sitio", "Copiar URL del admin".
 *   3. Servicios externos — Railway, Stripe, Cloudflare, GitHub, Mux,
 *      PostHog, Resend (un click → nueva pestaña).
 *
 * Filtra por `requireRole` para no mostrar items que el admin no puede usar.
 * El payload de items es controlado por el caller (AdminLayout) — así reusa
 * `navSections` como única fuente de verdad y no se duplica la nav.
 */

import { useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import {
  Cloud,
  CreditCard,
  ExternalLink,
  Eye,
  GitBranch,
  Link as LinkIcon,
  PlayCircle,
  Send,
  Telescope,
  Zap,
} from "lucide-react";
import type { UserRole } from "@/lib/auth";

export type PaletteItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Nombre del grupo del sidebar (Conversión, Sitio, Comercio, Sistema, …). */
  section: string;
  requireRole?: UserRole;
  end?: boolean;
};

type AdminCommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: PaletteItem[];
};

const EXTERNAL_SERVICES: { name: string; url: string; icon: LucideIcon; hint?: string }[] = [
  { name: "Railway · deploys + envs",         url: "https://railway.app/project/1426105f-1c6f-46c8-ab79-181b51e7d218",         icon: Cloud,       hint: "Railway" },
  { name: "Stripe Dashboard",                 url: "https://dashboard.stripe.com",                                            icon: CreditCard,  hint: "Stripe" },
  { name: "Cloudflare · worker + DNS",        url: "https://dash.cloudflare.com",                                             icon: Zap,         hint: "Cloudflare" },
  { name: "GitHub · Actions",                 url: "https://github.com/newbiepa/anaafernandez-main/actions",                  icon: GitBranch,   hint: "GitHub" },
  { name: "Mux · video assets",               url: "https://dashboard.mux.com",                                               icon: PlayCircle,  hint: "Mux" },
  { name: "PostHog · sections, CTAs & revenue", url: "https://us.posthog.com/project/422815/dashboard/1805289",             icon: Telescope,   hint: "PostHog" },
  { name: "Resend · transactional email",     url: "https://resend.com/emails",                                               icon: Send,        hint: "Resend" },
];

export function AdminCommandPalette({ open, onOpenChange, items }: AdminCommandPaletteProps) {
  const navigate = useNavigate();

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  const openExternal = (url: string) => {
    onOpenChange(false);
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyAdminUrl = async () => {
    onOpenChange(false);
    if (typeof window === "undefined" || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      /* clipboard ignored — non-critical */
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Buscar página, acción o servicio…" />
      <CommandList>
        <CommandEmpty>Sin resultados.</CommandEmpty>

        <CommandGroup heading="Páginas del admin">
          {items.map((item) => (
            <CommandItem
              key={item.to}
              value={`${item.section} ${item.label} ${item.to}`}
              onSelect={() => go(item.to)}
            >
              <item.icon className="mr-2 h-4 w-4 opacity-70" />
              <span>{item.label}</span>
              <CommandShortcut>{item.section}</CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Acciones rápidas">
          <CommandItem
            value="ver sitio publico home"
            onSelect={() => openExternal("/")}
          >
            <Eye className="mr-2 h-4 w-4 opacity-70" />
            <span>Ver sitio</span>
            <CommandShortcut>↗</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="copiar url admin"
            onSelect={() => void copyAdminUrl()}
          >
            <LinkIcon className="mr-2 h-4 w-4 opacity-70" />
            <span>Copiar URL del admin</span>
            <CommandShortcut>⌘C</CommandShortcut>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Servicios externos">
          {EXTERNAL_SERVICES.map(({ name, url, icon: Icon, hint }) => (
            <CommandItem
              key={url}
              value={`externo ${hint ?? ""} ${name}`}
              onSelect={() => openExternal(url)}
            >
              <Icon className="mr-2 h-4 w-4 opacity-70" />
              <span>{name}</span>
              <CommandShortcut>
                <ExternalLink className="inline h-3 w-3" />
              </CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
